/**
 * スマホの表を縦積みカードにするための補助(2026-09-07 指示: 全ページ横スクロールなし)。
 *
 * layout.css の「スマホの表は縦積み」は、各セル(td)の左に見出しを出すために
 * td の data-label を使う。ここでは見出し行(thead の最後の tr)の文字を、
 * 同じ列の td へ data-label として写す。見出しの無い列(操作ボタンなど)や
 * 列をまたぐセル(colSpan)には付けない。
 *
 * React が画面を描き直しても付け直せるよう、DOM の変化を監視して都度やり直す
 * (属性の変更は監視しないので、自分の書き換えでループしない)。
 * 横に並べたままにしたい表は table に .no-stack を付ける。
 */
import { useEffect } from 'react';

/** 見出しの無い表(項目名 | 説明 の2列など)の印。CSS 側で1列目を見出し扱いにする */
const KV_ATTR = 'data-kv';

export function labelTableCells(root: ParentNode = document.body): void {
  root.querySelectorAll<HTMLTableElement>('table').forEach((table) => {
    if (table.classList.contains('no-stack')) return;
    const head = table.tHead;
    const headRow = head && head.rows.length > 0 ? head.rows[head.rows.length - 1] : null;
    if (!headRow) {
      if (!table.hasAttribute(KV_ATTR)) table.setAttribute(KV_ATTR, '');
      return;
    }
    if (table.hasAttribute(KV_ATTR)) table.removeAttribute(KV_ATTR);

    const labels: string[] = [];
    for (const th of Array.from(headRow.cells)) {
      // 並べ替えの矢印(▲▼)・絞り込みの印(▾)などは見出しの文字から外す
      const text = (th.textContent ?? '')
        .replace(/[▲▼△▽↑↓⇅▾]+/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      for (let i = 0; i < Math.max(1, th.colSpan); i++) labels.push(text);
    }

    const sections: HTMLTableSectionElement[] = Array.from(table.tBodies);
    if (table.tFoot) sections.push(table.tFoot);
    for (const section of sections) {
      for (const tr of Array.from(section.rows)) {
        let col = 0;
        for (const cell of Array.from(tr.cells)) {
          const span = Math.max(1, cell.colSpan);
          const label = span === 1 ? (labels[col] ?? '') : '';
          if (label) {
            if (cell.getAttribute('data-label') !== label) cell.setAttribute('data-label', label);
          } else if (cell.hasAttribute('data-label')) {
            cell.removeAttribute('data-label');
          }
          col += span;
        }
      }
    }
  });
}

/** アプリ全体(モーダル含む)の表に見出しを写し続ける。AppLayout で一度だけ呼ぶ */
export function useStackedTables(): void {
  useEffect(() => {
    let scheduled = false;
    const run = () => {
      scheduled = false;
      labelTableCells(document.body);
    };
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(run);
    };
    run();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, []);
}
